/**
 * EmployeeDocuments - Document vault component for employee profile
 */
import { useState, useEffect } from 'react';
import { employeeDocumentsAPI } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { 
  FileText, Upload, Download, Trash2, CheckCircle, AlertTriangle, 
  Clock, Eye, Loader2, Shield, Calendar
} from 'lucide-react';

export default function EmployeeDocuments({ employeeId, canEdit = false }) {
  const [documents, setDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [uploadForm, setUploadForm] = useState({
    document_type: '',
    document_number: '',
    expiry_date: '',
    notes: '',
    file: null
  });

  useEffect(() => {
    if (employeeId) {
      loadDocuments();
      loadDocumentTypes();
    }
  }, [employeeId]);

  const loadDocuments = async () => {
    try {
      const res = await employeeDocumentsAPI.getDocuments(employeeId);
      setDocuments(res.data);
    } catch (error) {
      // May not have access or no documents
      console.error('Failed to load documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDocumentTypes = async () => {
    try {
      const res = await employeeDocumentsAPI.getTypes();
      setDocumentTypes(res.data);
    } catch (error) {
      console.error('Failed to load document types:', error);
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.document_type || !uploadForm.file) {
      toast.error('Please select document type and file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('document_type', uploadForm.document_type);
      formData.append('file', uploadForm.file);
      if (uploadForm.document_number) formData.append('document_number', uploadForm.document_number);
      if (uploadForm.expiry_date) formData.append('expiry_date', uploadForm.expiry_date);
      if (uploadForm.notes) formData.append('notes', uploadForm.notes);

      await employeeDocumentsAPI.uploadDocument(employeeId, formData);
      toast.success('Document uploaded successfully');
      setUploadOpen(false);
      setUploadForm({ document_type: '', document_number: '', expiry_date: '', notes: '', file: null });
      loadDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await employeeDocumentsAPI.downloadDocument(employeeId, doc.id);
      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete ${doc.document_type_name}?`)) return;
    
    try {
      await employeeDocumentsAPI.deleteDocument(employeeId, doc.id);
      toast.success('Document deleted');
      loadDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleVerify = async (doc) => {
    try {
      await employeeDocumentsAPI.verifyDocument(employeeId, doc.id);
      toast.success('Document verified');
      loadDocuments();
    } catch (error) {
      toast.error('Failed to verify document');
    }
  };

  const getStatusBadge = (doc) => {
    if (doc.is_expired) {
      return <Badge variant="destructive" className="gap-1"><AlertTriangle className="w-3 h-3" /> Expired</Badge>;
    }
    if (doc.expires_soon) {
      return <Badge variant="warning" className="gap-1 bg-amber-100 text-amber-800"><Clock className="w-3 h-3" /> Expiring Soon</Badge>;
    }
    if (doc.verified) {
      return <Badge variant="success" className="gap-1 bg-green-100 text-green-800"><CheckCircle className="w-3 h-3" /> Verified</Badge>;
    }
    return <Badge variant="secondary">Pending Verification</Badge>;
  };

  const selectedType = documentTypes.find(t => t.key === uploadForm.document_type);

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-blue-600" />
          <CardTitle className="text-lg">Documents</CardTitle>
          <Badge variant="outline">{documents.length}</Badge>
        </div>
        {canEdit && (
          <Button size="sm" onClick={() => setUploadOpen(true)}>
            <Upload className="w-4 h-4 mr-1" /> Upload
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {documents.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No documents uploaded yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {documents.map((doc) => (
              <div 
                key={doc.id} 
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  doc.is_expired ? 'border-red-200 bg-red-50' : 
                  doc.expires_soon ? 'border-amber-200 bg-amber-50' : 
                  'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    doc.is_expired ? 'bg-red-100' : 
                    doc.expires_soon ? 'bg-amber-100' : 
                    'bg-blue-100'
                  }`}>
                    <FileText className={`w-5 h-5 ${
                      doc.is_expired ? 'text-red-600' : 
                      doc.expires_soon ? 'text-amber-600' : 
                      'text-blue-600'
                    }`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{doc.document_type_name}</span>
                      {getStatusBadge(doc)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {doc.document_number && <span className="mr-3">#{doc.document_number}</span>}
                      {doc.expiry_date && (
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Expires: {doc.expiry_date.slice(0, 10)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={() => handleDownload(doc)} title="Download">
                    <Download className="w-4 h-4" />
                  </Button>
                  {canEdit && !doc.verified && (
                    <Button size="sm" variant="ghost" onClick={() => handleVerify(doc)} title="Verify" className="text-green-600">
                      <Shield className="w-4 h-4" />
                    </Button>
                  )}
                  {canEdit && (
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(doc)} title="Delete" className="text-destructive">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>Add a new document to the employee's vault</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Document Type *</Label>
              <Select 
                value={uploadForm.document_type} 
                onValueChange={(v) => setUploadForm({ ...uploadForm, document_type: v })}
              >
                <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {documentTypes.map((type) => (
                    <SelectItem key={type.key} value={type.key}>{type.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Document Number</Label>
              <Input 
                value={uploadForm.document_number}
                onChange={(e) => setUploadForm({ ...uploadForm, document_number: e.target.value })}
                placeholder="e.g., XXXX-XXXX-1234"
              />
            </div>

            {selectedType?.has_expiry && (
              <div className="space-y-2">
                <Label>Expiry Date</Label>
                <Input 
                  type="date"
                  value={uploadForm.expiry_date}
                  onChange={(e) => setUploadForm({ ...uploadForm, expiry_date: e.target.value })}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Notes</Label>
              <Input 
                value={uploadForm.notes}
                onChange={(e) => setUploadForm({ ...uploadForm, notes: e.target.value })}
                placeholder="Optional notes"
              />
            </div>

            <div className="space-y-2">
              <Label>File *</Label>
              <Input 
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
              />
              <p className="text-xs text-muted-foreground">PDF, JPG, PNG, DOC (max 10MB)</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>Cancel</Button>
            <Button onClick={handleUpload} disabled={uploading}>
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
